
import { FC, ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './GroupButtonList.scss'


type GroupButtonListType = {
    id: string;
    open: string | boolean;
    toggle: (open: string | boolean) => void;
    trigger?: 'hover' | 'click';
    placement?: "top" | "bottom" | "right" | "left" | "auto" | "top-start" | "top-end" | "bottom-start" | "bottom-end" | "right-start" | "right-end" | "left-start" | "left-end";
    children: React.ReactElement;
    buttons: { name: string, Icon?: ReactNode }[];
    disabledButtons?: string[];
    hiddenButtons?: string[];
    disabled?: boolean;
    onClick: (button: string) => void
}


const GroupButtonList: FC<GroupButtonListType> = ({ id, open, toggle, placement = 'right', children, buttons, onClick, disabledButtons = [], hiddenButtons = [], disabled }) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const overLayRef = useRef<HTMLDivElement>(null);
    const [overlayStyle, setOverlayStyle] = useState<React.CSSProperties>({
        position: 'fixed',
        zIndex: 99999,
        visibility: 'hidden',
    });
    const isOpen = open === id;

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isOpen) {
            toggle(false);
            return;
        }
        toggle(id);
    };

    useEffect(() => {
        const clickOutside = (event) => {
            if (!isOpen) return;
            else if (!event.target.closest('.group__button__trigger') && !event.target.closest('.group__button')) {
                toggle(false)
            }
            return
        }
        document.addEventListener('mousedown', clickOutside)
        return () => {
            document.removeEventListener('mousedown', clickOutside)
        }
    }, [isOpen, toggle])

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        const overlay = overLayRef.current;
        if (!trigger || !overlay || !isOpen) return;

        const triggerRect = trigger.getBoundingClientRect();
        const overlayRect = overlay.getBoundingClientRect();
        const { innerWidth, innerHeight } = window;
        const gap = 6;

        let top = triggerRect.top;
        let left = placement === 'left'
            ? triggerRect.left - overlayRect.width - gap
            : triggerRect.right + gap;

        // Horizontal overflow check & flip logic
        if (placement === 'left') {
            if (left < gap) {
                // If not enough space on left, try right
                const leftAlt = triggerRect.right + gap;
                if (leftAlt + overlayRect.width <= innerWidth - gap) {
                    left = leftAlt;
                } else {
                    left = gap; // fallback to clamp
                }
            }
        } else {
            // default right
            if (left + overlayRect.width > innerWidth - gap) {
                // If not enough space on right, try left
                const leftAlt = triggerRect.left - overlayRect.width - gap;
                if (leftAlt >= gap) {
                    left = leftAlt;
                } else {
                    left = innerWidth - overlayRect.width - gap; // fallback to clamp
                }
            }
        }

        // Vertical overflow check
        if (top + overlayRect.height > innerHeight - gap) {
            top = innerHeight - overlayRect.height - gap;
        }
        if (top < gap) top = gap;

        setOverlayStyle({
            position: 'fixed',
            top,
            left,
            zIndex: 99999,
            visibility: 'visible',
        });
    }, [isOpen, placement]);

    useLayoutEffect(() => {
        if (!isOpen) {
            setOverlayStyle(prev => ({ ...prev, visibility: 'hidden' }));
            return;
        }

        updatePosition();

        const resizeObserver = new ResizeObserver(() => updatePosition());
        if (triggerRef.current) resizeObserver.observe(triggerRef.current);
        if (overLayRef.current) resizeObserver.observe(overLayRef.current);

        window.addEventListener('resize', updatePosition);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen, updatePosition]);

    const getOverlayStyle = (): React.CSSProperties => overlayStyle;


    const overlay = isOpen ? ReactDOM.createPortal(
        <div
            id={id}
            ref={overLayRef}
            className="group__button__list__container"
            style={getOverlayStyle()}
            onClick={(e) => e.stopPropagation()}
        >
            {buttons.map(btn => {
                if (hiddenButtons.includes(btn.name)) return null;
                return (
                    <button key={btn.name}
                        onClick={(e) => {
                            e.stopPropagation();
                            onClick(btn.name);
                            toggle(false);
                        }}
                        className='group__button'
                        disabled={disabledButtons.includes(btn.name)}
                    >
                        {btn.Icon}
                        {btn.name}
                    </button>
                );
            })}
        </div>,
        document.body
    ) : null;

    return (
        <div
            ref={triggerRef}
            className={`group__button__trigger ${disabled ? 'disabled' : ''}`}
            onClick={handleToggle}
        >
            {children}
            {overlay}
        </div>
    );
}

export default GroupButtonList
